import { gql, useQuery } from '@apollo/client';
import ProductionByMonthStackedByTypeTotalChartThumbnail from '@assets/images/report-components/production-by-month-stacked-by-type-total-chart-thumbnail.png';
import { type Theme, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { GraphWrapper } from '@/admins/SmartReports/components/Components/Charts/GraphWrapper';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import {
  AssetAggregateByEnum,
  AssetAggregatesFieldsEnum,
  type AssetAggregatesStacked,
  AssetStackByEnum,
} from '@/types/GraphqlSchema';
import { createDateWithYearOffset } from '@/utils/dateFormatter';
import { getNestedValue } from '@/utils/helpers';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '../types';

export const COMPONENT_ID = 'production_by_month_stacked_by_type_total_chart';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        values
      }
      stacks
    }
  }
`;

enum ProductionType {
  Developed = 'pdp',
  Undeveloped = 'pud',
}

enum ProductionCategory {
  Oil = 'Oil',
  Gas = 'Gas',
  Condensate = 'Condensate',
}

const PRODUCTION_CATEGORIES = {
  oil: {
    name: ProductionCategory.Oil,
    field: 'oilProductionsSum',
  },
  gas: {
    name: ProductionCategory.Gas,
    field: 'gasProductionsBoedSum',
  },
  condensate: {
    name: ProductionCategory.Condensate,
    field: 'condensateProductionsSum',
  },
};

const COLOR_MAP = {
  [ProductionCategory.Oil]: 'palette.chartColors.earthGreen.c100',
  [ProductionCategory.Gas]: 'palette.chartColors.trueRed.c100',
  [ProductionCategory.Condensate]: 'palette.chartColors.amber.c100',
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { data, loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(
    QUERY,
    {
      variables: {
        input: {
          by: AssetAggregateByEnum.ProductionMonth,
          fields: [
            AssetAggregatesFieldsEnum.OilProductionsSum,
            AssetAggregatesFieldsEnum.GasProductionsBoedSum,
            AssetAggregatesFieldsEnum.CondensateProductionsSum,
          ],
          stackBy: AssetStackByEnum.ProductionType,
          portfolio: {
            ...portfolio,
            productionTypes: Object.values(ProductionType),
            startDate: createDateWithYearOffset(-50),
            endDate: createDateWithYearOffset(50),
          },
        },
      },
      skip: !!persistedData,
    },
  );

  useEffect(() => {
    if (data?.assetAggregatesStacked) {
      const chartData = data.assetAggregatesStacked;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

type TickData = {
  tickVals: string[];
  tickText: string[];
};

const extractXAxisData = (data: AssetAggregatesStacked): string[] => {
  return data.data?.map(item => item.group) || [];
};

const generateXAxisTicks = (xAxisData: string[]): TickData => {
  const tickVals: string[] = [];
  const tickText: string[] = [];

  xAxisData.forEach((dateStr, index) => {
    if (index % 36 === 0) {
      // Every 36 months (3 years)
      tickVals.push(dateStr);
      const year = new Date(dateStr).getFullYear();
      tickText.push(year.toString());
    }
  });

  return { tickVals, tickText };
};

const formatChartData = (data: AssetAggregatesStacked, theme: Theme) => {
  try {
    const { data: chartData } = data;

    return Object.values(PRODUCTION_CATEGORIES).map((category, index) => {
      const x = chartData.map(item => item.group);
      const y = chartData.map(item => {
        // Sum PDP and PUD values together
        const pdpValue = item.values[ProductionType.Developed]?.[category.field] || 0;
        const pudValue = item.values[ProductionType.Undeveloped]?.[category.field] || 0;
        return pdpValue + pudValue;
      });

      const color = getNestedValue(theme, COLOR_MAP[category.name]);

      return {
        x,
        y,
        type: 'scatter',
        mode: 'lines',
        name: category.name,
        stackgroup: 'one',
        fillcolor: color,
        line: { color, width: 0 },
        legendrank: Object.values(PRODUCTION_CATEGORIES).length - index,
      };
    });
  } catch {
    return null;
  }
};

const calculateMaxYForStackedData = (data: AssetAggregatesStacked) => {
  try {
    const { data: chartData } = data;

    // Calculate the cumulative maximum by summing all production categories (PDP + PUD) for each time period
    const cumulativeMaxY = chartData.reduce((maxSoFar, item) => {
      const totalForThisPeriod = Object.values(PRODUCTION_CATEGORIES).reduce((sum, category) => {
        const pdpValue = item.values[ProductionType.Developed]?.[category.field] || 0;
        const pudValue = item.values[ProductionType.Undeveloped]?.[category.field] || 0;
        return sum + pdpValue + pudValue;
      }, 0);

      return Math.max(maxSoFar, totalForThisPeriod);
    }, 0);

    return cumulativeMaxY;
  } catch {
    return undefined;
  }
};

const ComponentView = ({
  data,
  mode = ComponentViewMode.View,
}: ComponentViewProps<AssetAggregatesStacked>) => {
  const theme = useTheme();
  const formattedData = useMemo(() => formatChartData(data, theme), [data, theme]);
  const maxY = useMemo(() => calculateMaxYForStackedData(data), [data]);

  const { tickVals, tickText } = useMemo(() => {
    const xAxisData = extractXAxisData(data);
    return generateXAxisTicks(xAxisData);
  }, [data]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Production (Liquids and Gas)</ComponentTitle>
      <GraphWrapper
        data={formattedData}
        layout={getLayout({
          xaxis: { tickvals: tickVals, ticktext: tickText },
          yaxis: {
            title: { text: 'boe/d' },
            ...(maxY ? { range: [0, maxY * 1.1] } : {}),
          },
          legend: { y: -0.3 },
        })}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Production (Liquids and Gas)
    </ComponentTitle>
    <img
      src={ProductionByMonthStackedByTypeTotalChartThumbnail}
      alt='Production (Liquids and Gas) Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const ProductionByMonthStackedByTypeTotalChartComponent: ComponentDefinition<AssetAggregatesStacked> =
  {
    id: COMPONENT_ID,
    name: 'Production (Liquids and Gas)',
    type: SmartReportComponentTypesEnum.Chart,
    reportTypes: [SmartReportTypeEnum.Asset],
    Thumbnail: ComponentThumbnail,
    Component: ComponentEdit,
    ComponentView,
  };
